import {DESKTOP_BREAKPOINT} from '../../common/scripts/constants';
import Observer from '../../common/scripts/observer';
import ScrollAnimation from '../scrollAnimation';
import {Utils} from '../../common/scripts/utils';

const observer = new Observer();

class AbstractAccordion {
    constructor() {
        /**
         * Css-класс для открытого состояния
         * @type {String}
         */
        this.openClass = 'is-open';

        /**
         * Состояние компонента
         * true - открыто
         * false - закрыто
         * @type {Boolean}
         */
        this.isOpen = false;

        /**
         * Высота закрытого блока
         * Всегда одинаковая
         * @type {Number}
         */
        this.closedHeight = 0;

        /**
         * Высота схлопываемого контента
         * Зависит от содержимого
         * @type {Number}
         */
        this.height = null;

        /**
         * Целевой элемент
         * @type {Node}
         */
        this.elem = null;

        /**
         * Элемент-переключатель
         * @type {Node}
         */
        this.toggler = null;

        /**
         * Элемент-содержимое
         * Используем его высоту
         * @type {Node}
         */
        this.content = null;

        /**
         * Элемент-обертка содержимого
         * Его высоту меняем
         * @type {Node}
         */
        this.contentOuter = null;

        /**
         * Элемент заголовка
         * @type {Node}
         */
        this.title = null;

        /**
         * Заголовок для переключения между (заголовок/скрыть)
         * @type {String}
         */
        this.titleText = '';

        /**
         * Текст "скрыть"
         * @type {String}
         */
        this.hiddenText = 'Скрыть';

        /**
         * Флаг, менять ли заголовок между (заголовок/скрыть)
         * @type {Boolean}
         */
        this.changeTitle = false;

        /**
         * Блок, к которому нужно прокрутить при закрытии
         * @type {Node}
         */
        this.blockSelector = null;

        /**
         * Флаг задержки скрытия контентной части
         * @type {Boolean}
         */
        this.closeTimeout = false;

        /**
         * Значение задержки перед скрытием контентной части
         * @type {Number}
         */
        this.timeout = 300;

        // Привязываем контекст
        this.close = this.close.bind(this);
        this.open = this.open.bind(this);
    }

    /**
     * Инициализирует модуль
     * @param {Object} options - параметры
     */
    init(options = {}) {
        this._setElements(options);
        this._setInitState();
        this._subscribe();
        this._bindEvents();
    }

    /**
     * Переинициирует начальное состояние
     */
    reinit() {
        this._setInitState();
    }

    /**
     * Определяет элементы из параметров
     * @param {Object} options - параметры
     * @private
     */
    _setElements(options) {
        const {selectors} = options;

        this.elem = options.elem;
        this.closeTimeout = options.closeTimeout;
        this.toggler = options.elem.querySelector(selectors.toggler);
        this.content = options.elem.querySelector(selectors.content);
        this.contentOuter = options.elem.querySelector(selectors.contentOuter);
        this.title = options.elem.querySelector(selectors.title);
        this.blockElement = options.elem.closest(selectors.blockElement);
    }

    /**
     * Ставит начальное состояние
     * @private
     */
    _setInitState() {
        this.height = this._getHeight();
        this.isOpen = this.elem.classList.contains(this.openClass);
        this.titleText = this.title ? this.elem.dataset.initialTitle : false;
        this.changeTitle = Boolean(this.elem.dataset.changeTitle);
        this.hiddenText = this.elem.dataset.alternateTitle || this.hiddenText;

        if (this.title && this.titleText) {
            this.title.innerHTML = this.titleText;
        }
    }

    /**
     * Возвращает высоту контента
     * @returns {Number} - высота контента
     * @private
     */
    _getHeight() {
        return this.content.offsetHeight;
    }

    /**
     * Подписывается на события других модулей
     * ATTENTION! Абстрактный метод
     * @private
     */
    _subscribe() {
        throw new Error('Метод _subscribe должен быть переопределен');
    }

    /**
     * Навешивает обработчики событий
     * @private
     */
    _bindEvents() {
        this.toggler.addEventListener('click', this._onTogglerClick.bind(this));

        const events = ['resize', 'orientationchange'];

        events.forEach((event) => {
            window.addEventListener(event, this._onResize.bind(this));
        });
    }

    /**
     * Событие, которое происходит при нажатии на переключатель
     * @private
     */
    _onTogglerClick() {
        const method = this.isOpen ? this.close : this.open;

        this.height = this._getHeight();

        method();
    }

    /**
     * Событие, которое происходит при изменении размера/ориентации окна
     * @private
     */
    _onResize() {
        if (this.isOpen) {
            this.update();
        }
    }

    /**
     * Установка высоты элементу-обертке
     * @param {Number} height - высота
     * @private
     */
    _setContentHeight(height) {
        this.contentOuter.style.height = height ? `${height}px` : height;
    }

    /**
     * Открывает контент
     */
    open() {
        if (this.changeTitle && this.titleText) {
            Utils.clearHtml(this.title);
            Utils.insetContent(this.title, this.hiddenText);
        }

        this.elem.classList.add(this.openClass);
        this.content.classList.add(this.openClass);

        this.isOpen = true;
        this._setContentHeight(this.height);
    }

    /**
     * Закрывает контент
     */
    close() {
        if (this.changeTitle && this.titleText) {
            Utils.clearHtml(this.title);
            Utils.insetContent(this.title, this.titleText);
        }

        if (this.blockElement) {
            this._scrollToElement();
        }

        setTimeout(() => {
            this.elem.classList.remove(this.openClass);
            this.content.classList.remove(this.openClass);
            this.isOpen = false;
            this._setContentHeight(this.closedHeight);
        }, this.closeTimeout ? this.timeout : 0);
    }

    /**
     * Обновляет высоту
     */
    update() {
        this.height = this._getHeight();
        this._setContentHeight(this.isOpen ? this.height : this.closedHeight);
    }

    /**
     * Скролл к началу блока
     */
    _scrollToElement() {
        if (!this.blockElement) {
            return;
        }

        const targetY =
            this.blockElement.getBoundingClientRect().top + pageYOffset - (this.blockElement.offsetTop * 2);

        const scrollAnimation = new ScrollAnimation({
            targetY
        });

        scrollAnimation.scroll();
    }
}

export class Accordion extends AbstractAccordion {
    /**
     * Ставит начальное состояние
     * @private
     */
    _setInitState() {
        super._setInitState();
        this._setContentHeight(this.isOpen ? this.height : this.closedHeight);
    }

    /**
     * Подписывается на события других модулей
     * @private
     */
    _subscribe() {
        observer.subscribe('closeAccordion', this.close);
        observer.subscribe('openAccordion', this.open);
    }
}

export class MobileAccordion extends AbstractAccordion {
    /* @Override */
    init(options = {}) {
        this.breakpoint = options.breakpoint || DESKTOP_BREAKPOINT;
        super.init(options);
    }

    /**
     * Определяет, десктоп или нет
     * @returns {boolean} - true - десктоп
     * @private
     */
    _isDesktop() {
        return Utils.isBreakpoint(this.breakpoint, 10000);
    }

    /**
     * Ставит начальное состояние
     * @private
     */
    _setInitState() {
        super._setInitState();

        if (this._isDesktop()) {
            return;
        }

        this._setContentHeight(this.isOpen ? this.height : this.closedHeight);
    }

    /**
     * Подписывается на события других модулей
     * @private
     */
    _subscribe() {
        observer.subscribe('openMobileAccordion', this.close);
    }

    /**
     * Событие, которое происходит при изменении размера/ориентации окна
     * @private
     */
    _onResize() {
        this.update();
    }

    /**
     * Открывает контент
     */
    open() {
        if (this._isDesktop()) {
            return;
        }

        observer.publish('openMobileAccordion');

        super.open();
    }

    /**
     * Закрывает контент
     */
    close() {
        if (this._isDesktop()) {
            return;
        }

        super.close();
    }

    /**
     * Обновляет высоту
     */
    update() {
        if (this._isDesktop()) {
            this.height = this._getHeight();
            this._setContentHeight(null);

            return;
        }

        super.update();
    }
}
